import React, { useState, useEffect, useMemo } from 'react'
import './reports-and-statistics.css'
import { HeaderHLPure, SimpleLoading } from '../../pures';
import { Container, Grid } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { AgeAndGenderGraph, CPChoroplethMap, DailyGraph, DashboardDateFilter, HourlyGraph, TicketVolumeGraph, StatCard, UsageStatistics } from '../../pures/dashboard';
import moment from 'moment'

const getDatesRange = (startDate, endDate, allMonths, periodType) => {
    // Convertir las fechas a objetos Date
    const start = new Date(startDate);
    const end = new Date(endDate);

    // Obtener mes y año de inicio
    const startMonth = start.getMonth();
    const startYear = start.getFullYear();

    // Obtener mes y año de fin
    const endMonth = end.getMonth();
    const endYear = end.getFullYear();

    // Calcular la diferencia en años
    const yearDifference = endYear - startYear;

    // Crear arreglo con los meses, índices y años dentro del rango
    const monthsInRange = [];
    let currentYear = startYear;

    switch (periodType) {
        case "year":
            for (let i = startMonth; i <= endMonth + (yearDifference * 12); i++) {
                const monthIndex = i % 12;
                const monthName = allMonths[monthIndex];

                // Determinar el año actual
                if (monthIndex === 0 && i !== startMonth) {
                    currentYear++; // Incrementar año cuando pasamos de diciembre a enero
                }

                monthsInRange.push({
                    name: monthName,
                    index: monthIndex,
                    year: currentYear
                });
            }
            break;

        case "month":
            const mesEncontrado = allMonths.find((mes, index) => index === (startMonth));

            if (mesEncontrado) {
                const monthIndex = allMonths.indexOf(mesEncontrado);

                monthsInRange.push({
                    name: mesEncontrado,
                    index: monthIndex,
                    year: startYear,
                });
            }
            break;
    }

    return monthsInRange;
};

function ReportsAndStatisticsView(props) {
    const {
        isLoading,
        dates,
        handleChangeDate,
        handleSearch,
        data,
        periodType
    } = props;

    const { t, i18n } = useTranslation('clients-dashboard', { keyPrefix: 'reportsScreen' });
    const { t: tCommon } = useTranslation('common');

    const [days, setDays] = useState([]);
    const [longDays, setLongDays] = useState([]);
    const [months, setMonths] = useState([]);
    const [longMonths, setLongMonths] = useState([]);
    const [prevMonths, setPrevMonths] = useState([]);
    const [prevLongMonths, setPrevLongMonths] = useState([]);

    useEffect(() => {
        let monthsList, longMonthsList = []

        const loadDateRanges = () => {
            moment.locale(i18n.language === 'en' ? 'en' : 'es')

            setDays(moment.weekdaysShort())
            setLongDays(moment.weekdays())
            monthsList = moment.monthsShort()
            longMonthsList = moment.months()

            setMonths(getDatesRange(dates.startDate || '', dates.endDate || '', monthsList, periodType))
            setLongMonths(getDatesRange(dates.startDate || '', dates.endDate || '', longMonthsList, periodType))

            if (dates.startDate && dates.endDate) {
                let prevStart = new Date(dates.startDate);
                let prevEnd = new Date(dates.endDate);

                if (periodType === "year") {
                    prevStart.setFullYear(prevStart.getFullYear() - 1);
                    prevEnd.setFullYear(prevEnd.getFullYear() - 1);
                } else {
                    prevStart.setMonth(prevStart.getMonth() - 1);
                    prevEnd.setMonth(prevEnd.getMonth() - 1);
                }

                setPrevMonths(getDatesRange(prevStart || '', prevEnd || '', monthsList, periodType));
                setPrevLongMonths(getDatesRange(prevStart || '', prevEnd || '', longMonthsList, periodType));
            }
        };

        loadDateRanges();

    }, [i18n.language, dates, periodType])

    const { tickFormat, tickLongFormat, prevTickFormat, prevTickLongFormat } = useMemo(() => {
        if (periodType === 'year' || periodType === 'month') {
            return {
                tickFormat: months,
                tickLongFormat: longMonths,
                prevTickFormat: prevMonths,
                prevTickLongFormat: prevLongMonths
            };
        } else if (periodType === 'week') {
            return {
                tickFormat: days,
                tickLongFormat: longDays,
                prevTickFormat: days,
                prevTickLongFormat: longDays
            };
        }

        return {
            tickFormat: null,
            tickLongFormat: null,
            prevTickFormat: null,
            prevTickLongFormat: null
        };
    }, [periodType, months, days, longMonths, prevMonths, prevLongMonths])

    return (
        <>
            {isLoading
                ? <div className='loading'>
                    <SimpleLoading />
                </div>
                : (<div className={"dashboardView"}>
                    <Container>
                        <Grid container spacing={3}>
                            <Grid item xs={12}>
                                <HeaderHLPure title={t("title")} />
                            </Grid>

                            <Grid item xs={12} className="reportsAndStatisticsView-dashboard">
                                <div className='rasd-stats'>
                                    {/* DATE FILTER */}
                                    <DashboardDateFilter dates={dates} handleChangeDate={handleChangeDate} handleSearch={handleSearch} />

                                    {/* NICHE */}
                                    <StatCard
                                        title={t("niche")}
                                        value={tCommon(data?.niche)}
                                    />

                                    {/* AVERAGE AGE */}
                                    <StatCard
                                        title={t("averageAge")}
                                        value={data?.averageAge ? <>{data.averageAge.toFixed(0)} <span>{t("years")}</span></> : "N/D"}
                                    />

                                    {/* AVERAGE TOTAL */}
                                    <StatCard
                                        title={t("averageTotal")}
                                        value={data?.averageAmount ? <>${data.averageAmount.toFixed(2)} <span className="small-value">/{t("month")}</span></> : "N/D"}
                                    />
                                </div>
                                <div className='rasd-map dashboard-graph-card'>
                                    <p className="dashboardView-card-title">{t("consumerConcentration")}</p>
                                    <CPChoroplethMap data={data?.mapData} t={t} />
                                </div>
                                <div className="rasd-age">
                                    <AgeAndGenderGraph title={t("ageAndGender")} data={data?.genderAndAge} />
                                </div>

                                <TicketVolumeGraph
                                    data={data?.ticket_volume}
                                    prevData={data?.ticket_volume_prev}
                                    tickFormat={tickFormat}
                                    tickLongFormat={tickLongFormat}
                                    periodType={periodType}
                                    startDate={dates.startDate}
                                    prevTickFormat={prevTickFormat}
                                    prevTickLongFormat={prevTickLongFormat}
                                    t={t} // Pasa la función de traducción
                                    key={`${i18n.language}-${periodType}`} // Fuerza recreación al cambiar idioma
                                    i18n={i18n}
                                />

                                <DailyGraph data={data?.daily} days={days} longDays={longDays} />

                                <HourlyGraph data={data?.hourly} />
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <UsageStatistics t={t}
                                    title={t("activeOffers")}
                                    progressValue={data?.offers_percentage}
                                    progressLabel={t("ofBusinessesPublished", { type: t("offers").toLowerCase() })}
                                    values={data?.avg_offers}
                                />
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <UsageStatistics t={t}
                                    title={t("activeSurveys")}
                                    progressValue={data?.surveys_percentage}
                                    progressLabel={t("ofBusinessesPublished", { type: t("surveys").toLowerCase() })}
                                    values={data?.avg_surveys}
                                />
                            </Grid>
                        </Grid>
                    </Container>
                </div>)
            }
        </>
    )
}

export default ReportsAndStatisticsView